import React, { useState, useCallback, useRef } from 'react';
import { useDropzone, DropzoneOptions } from 'react-dropzone';
import { Box, Button, TextField, Autocomplete, Typography, LinearProgress, Alert } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { uploadDataset, createFileJob, getJobStatus } from '../../services/api';
import { useChat } from '../../context/chatContext';

interface FileUploadProps {
  knowledgebaseName: string;
  onKnowledgebaseNameChange: (name: string) => void;

  // BACKWARD-COMPAT: keep original success callback
  onUploadSuccess: () => void;

  // NEW (optional): notify when a job gets queued and when it finishes
  onJobQueued?: (queuedJobId: string) => void;
  onUploadFinished?: (args: { jobId: string; status: 'completed' | 'failed' }) => void;

  existingDatasets: string[];
  userId: string | null | undefined;
  setIsLoading: (isLoading: boolean) => void;
  orgDatasets?: boolean;
}

type JobStatus = 'queued' | 'uploading' | 'processing' | 'completed' | 'failed';

const FileUpload: React.FC<FileUploadProps> = ({
  knowledgebaseName,
  onKnowledgebaseNameChange,
  onUploadSuccess,
  onJobQueued,
  onUploadFinished,
  existingDatasets,
  userId,
  setIsLoading,
  orgDatasets = false,
}) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [jobId, setJobId] = useState<string | null>(null);
  const [status, setStatus] = useState<JobStatus | null>(null);

  const pollTimer = useRef<number | null>(null);
  const { setSessionId, setToken } = useChat();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(prevFiles => [...prevFiles, ...acceptedFiles]);
  }, []);
  const onDragEnter = useCallback(() => {}, []);
  const onDragOver = useCallback(() => {}, []);
  const onDragLeave = useCallback(() => {}, []);

  const dropzoneOptions: DropzoneOptions = {
    onDrop,
    onDragEnter,
    onDragOver,
    onDragLeave,
    multiple: true,
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone(dropzoneOptions);

  const clearPoll = () => {
    if (pollTimer.current) {
      window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  };

  const pollStatus = async (uid: string, jid: string) => {
    try {
      const res = await getJobStatus(uid, jid, setSessionId, setToken);
      if (res?.status) setStatus(res.status as JobStatus);
      if (typeof res?.progress === 'number' && res.status === 'uploading') {
        // If backend reports upload stage progress, reflect it
        setUploadProgress(Math.max(0, Math.min(100, Math.round(res.progress))));
      }

      if (res.status === 'completed' || res.status === 'failed') {
        clearPoll();
        setIsLoading(false);
        if (res.status === 'completed') {
          setSuccess('Files uploaded and processed successfully!');
          onUploadSuccess?.();
        }
        onUploadFinished?.({ jobId: jid, status: res.status });
        setUploading(false);
        return;
      }

      // continue polling
      pollTimer.current = window.setTimeout(() => pollStatus(uid, jid), 1500) as unknown as number;
    } catch (err) {
      // if polling fails, try again a little later unless we've finished
      pollTimer.current = window.setTimeout(() => pollStatus(uid, jid), 2500) as unknown as number;
    }
  };

  const handleUpload = async () => {
    if (files.length === 0 || !knowledgebaseName) {
      setError('Please select files and provide a knowledgebase name.');
      return;
    }
    if (!userId) {
      setError('No user session. Please refresh and try again.');
      return;
    }

    setUploading(true);
    setError(null);
    setSuccess(null);
    setUploadProgress(0);
    setStatus('queued');
    setIsLoading(true);

    clearPoll();

    try {
      // 1) Create a job
      const job = await createFileJob(
        userId,
        { kind: 'dataset', name: knowledgebaseName, orgDatasets: !!orgDatasets },
        setSessionId,
        setToken
      );
      const jid = job.job_id; // <- only job_id exists
      if (!jid) throw new Error('Failed to create upload job.');
      setJobId(jid);
      onJobQueued?.(jid);

      // 2) Start polling status immediately
      pollTimer.current = window.setTimeout(() => pollStatus(userId, jid), 1000) as unknown as number;

      // 3) Upload files with progress + jobId
      await uploadDataset(
        knowledgebaseName,
        files,
        userId,
        orgDatasets,
        setSessionId,
        setToken,
        {
          jobId: jid,
          onProgress: (pct) => {
            setStatus('uploading');
            setUploadProgress(pct);
          }
        }
      );

      // After upload finishes, backend will flip job status to "processing" and then "completed".
      // Our poller will catch that and fire onUploadSuccess/onUploadFinished & stop isLoading.

      // Clear file list on client once we’ve handed them off.
      setFiles([]);
    } catch (err: any) {
      clearPoll();
      setUploading(false);
      setIsLoading(false);
      setStatus('failed');
      setError(err?.message || 'Upload failed.');
    }
  };

  React.useEffect(() => {
    return () => {
      clearPoll();
    };
  }, []);

  return (
    <Box>
      <Autocomplete
        freeSolo
        options={existingDatasets}
        value={knowledgebaseName}
        onChange={(_, newValue) => onKnowledgebaseNameChange(newValue || '')}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Knowledgebase Name"
            variant="outlined"
            margin="normal"
            onChange={(e) => onKnowledgebaseNameChange(e.target.value)}
          />
        )}
      />

      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          borderRadius: 1,
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? 'action.hover' : 'transparent',
          transition: 'background-color 0.2s ease-in-out',
          height: '200px',
          mb: 2,
        }}
      >
        <input {...(getInputProps() as React.HTMLProps<HTMLInputElement>)} />
        <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
        <Typography>Drag & drop files here, or click to select files</Typography>
      </Box>

      {files.length > 0 && (
        <Box>
          <Typography variant="subtitle2">Selected files:</Typography>
          <ul>
            {files.map((file, i) => (
              <li key={i}>{file.name}</li>
            ))}
          </ul>
        </Box>
      )}

      <Button
        variant="contained"
        onClick={handleUpload}
        disabled={uploading || files.length === 0 || !knowledgebaseName}
        fullWidth
      >
        {uploading ? (status ? `Uploading (${status})...` : 'Uploading...') : 'Upload'}
      </Button>

      {(uploading || status === 'uploading') && (
        <LinearProgress variant="determinate" value={uploadProgress} sx={{ mt: 2 }} />
      )}

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
      {jobId && (
        <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
          Job ID: {jobId}
        </Typography>
      )}
    </Box>
  );
};

export default FileUpload;
